import {
    Color3,
    Color4,
    Constants,
    PostProcess,
    RenderTargetTexture,
    ShaderLanguage,
    StandardMaterial,
    type AbstractMesh,
    type Camera,
    type Observer,
    type Scene,
} from '@babylonjs/core';
import { MESH_OUTLINE_COLOR_SLOTS, MESH_OUTLINE_MAX_REACH, registerMeshOutlineShaders } from './mesh-outline-shaders';

/**
 * Which ring a layer draws: `effect` is the first ring, right at the
 * silhouette (Unveil's violet); `target` is the second, just outside the
 * effect ring where a body has both, else right at the silhouette (a
 * hovered item).
 */
export type MeshOutlineRank = 'effect' | 'target';

/** Each ring's width, in screen pixels. */
export type MeshOutlineWidths = Readonly<Record<MeshOutlineRank, number>>;

/** The ring round something the player can hover and act on: a dropped item, a chest, a breakable prop. */
export const INTERACTABLE_OUTLINE_COLOR = Color3.FromHexString('#ffe7a3');

/** Rings stay thin: a 1 px effect ring and a 2 px target ring outside it. */
export const DEFAULT_MESH_OUTLINE_WIDTHS: MeshOutlineWidths = { effect: 1, target: 2 };

/** One effect's outlines, set together. */
export interface MeshOutlineLayer {
    /**
     * Outlines `meshes` in `color`, mixed over the scene by `alpha` (0 to
     * 1); meshes left out lose this layer's outline. Each character's meshes
     * (body, gear, eyes) outline as one silhouette.
     */
    set(meshes: readonly AbstractMesh[], color: Color3, alpha?: number): void;
    /** Drops every outline this layer asks for. */
    clear(): void;
    /** Drops its outlines and the layer itself, for good. */
    dispose(): void;
}

interface LayerState {
    readonly rank: MeshOutlineRank;
    meshes: readonly AbstractMesh[];
    color: Color3;
    alpha: number;
}

/** A mesh's place in the mask: each ring's color slot it is outlined in (1 to 4), or 0 for none. */
interface MaskFlags {
    readonly effect: number;
    readonly target: number;
}

/** A ring color: what a slot draws in. */
interface RingColor {
    readonly color: Color3;
    readonly alpha: number;
}

const sceneOutlines = new WeakMap<Scene, MeshOutlines>();
/** Antialiasing on the scene drawn under the rings, as the canvas has without them. */
const MSAA_SAMPLES = 4;
/** Each ring's color slots, 1 to 4, as the shader's uniforms are numbered. */
const SLOT_NUMBERS = Array.from({ length: MESH_OUTLINE_COLOR_SLOTS }, (_, index) => index + 1);

/**
 * The outlines round characters and items in a scene, drawn in screen space
 * so they trace each silhouette and never the shapes inside it (no rings
 * round the eyes, the eyebrows, or where gear meets the body) at an exact
 * pixel width, and two can stack: effects (Unveil) draw the first ring,
 * a hovered item the second, just outside it.
 *
 * Every outlined mesh is drawn into a mask (red: effect ring, green: target
 * ring) by a render target only they are in, then one post-process on the
 * camera draws the rings outside the mask (`mesh-outline-shaders.ts`). Both
 * exist only while something is outlined. Effects ask through layers; each
 * ring draws in up to four colors at once (one slot per distinct color, in
 * the mask), so two hovered or highlighted things keep their own; past
 * four, the rest share the fourth.
 */
export class MeshOutlines {
    private readonly layers: LayerState[] = [];
    private readonly flags = new Map<AbstractMesh, MaskFlags>();
    private widths: MeshOutlineWidths = DEFAULT_MESH_OUTLINE_WIDTHS;
    private mask: RenderTargetTexture | null = null;
    private postProcess: PostProcess | null = null;
    private camera: Camera | null = null;
    /** Whether the post-process is on its camera yet: only once its shader is ready. */
    private isAttached = false;
    private beforeRender: Observer<Scene> | null = null;
    private readonly maskMaterials = new Map<string, StandardMaterial>();
    private readonly ringColors: Record<MeshOutlineRank, RingColor[]> = { effect: [], target: [] };

    private constructor(private readonly scene: Scene) {
        registerMeshOutlineShaders();
        scene.onDisposeObservable.addOnce(() => this.release());
    }

    /** The scene's outlines, made the first time anything asks. */
    static of(scene: Scene): MeshOutlines {
        let outlines = sceneOutlines.get(scene);
        if (!outlines) {
            outlines = new MeshOutlines(scene);
            sceneOutlines.set(scene, outlines);
        }
        return outlines;
    }

    /** A new layer drawing the `rank` ring. */
    layer(rank: MeshOutlineRank): MeshOutlineLayer {
        const state: LayerState = { rank, meshes: [], color: Color3.White(), alpha: 1 };
        this.layers.push(state);
        return {
            set: (meshes, color, alpha = 1): void => {
                state.meshes = [...meshes];
                state.color = color;
                state.alpha = Math.min(1, Math.max(0, alpha));
                this.sync();
            },
            clear: (): void => {
                if (state.meshes.length === 0) return;
                state.meshes = [];
                this.sync();
            },
            dispose: (): void => {
                const index = this.layers.indexOf(state);
                if (index < 0) return;
                this.layers.splice(index, 1);
                if (state.meshes.length > 0) this.sync();
            },
        };
    }

    /** Changes the rings' widths (screen pixels); each is kept within what the post-process can reach. */
    setWidths(widths: Partial<MeshOutlineWidths>): void {
        const effect = Math.max(0, widths.effect ?? this.widths.effect);
        const target = Math.max(0, Math.min(widths.target ?? this.widths.target, MESH_OUTLINE_MAX_REACH - effect));
        this.widths = { effect, target };
    }

    /** How many layers are open, for tests and debugging. */
    get layerCount(): number {
        return this.layers.length;
    }

    /** Which rings outline `mesh` now, for tests and debugging. */
    findMaskFlags(mesh: AbstractMesh): MaskFlags | undefined {
        return this.flags.get(mesh);
    }

    /** A ring's color and alpha in a slot (1 to 4) now, for tests and debugging. */
    ringColor(rank: MeshOutlineRank, slot: number): RingColor | undefined {
        return this.ringColors[rank][slot - 1];
    }

    /** Whether the mask and post-process are running (only while something is outlined). */
    get isActive(): boolean {
        return this.postProcess !== null;
    }

    /** Works out every mesh's color slots and each ring's colors from the layers, and starts or stops drawing. */
    private sync(): void {
        const next = new Map<AbstractMesh, MaskFlags>();
        for (const rank of ['effect', 'target'] as const) {
            const colors: RingColor[] = [];
            for (const layer of this.layers) {
                if (layer.rank !== rank || layer.meshes.length === 0) continue;
                let slot = colors.findIndex((ring) => ring.color.equals(layer.color) && ring.alpha === layer.alpha) + 1;
                if (slot === 0) {
                    if (colors.length < MESH_OUTLINE_COLOR_SLOTS) {
                        colors.push({ color: layer.color.clone(), alpha: layer.alpha });
                    }
                    slot = colors.length;
                }
                for (const mesh of layer.meshes) {
                    if (mesh.isDisposed()) continue;
                    const current = next.get(mesh) ?? { effect: 0, target: 0 };
                    next.set(mesh, { ...current, [rank]: slot });
                }
            }
            this.ringColors[rank] = colors;
        }

        if (next.size === 0) {
            this.flags.clear();
            this.release();
            return;
        }
        const mask = this.ensureMask();
        for (const mesh of this.flags.keys()) {
            if (!next.has(mesh)) mask.setMaterialForRendering(mesh, undefined);
        }
        for (const [mesh, flags] of next) {
            const previous = this.flags.get(mesh);
            if (!previous || previous.effect !== flags.effect || previous.target !== flags.target) {
                mask.setMaterialForRendering(mesh, this.maskMaterial(flags));
            }
        }
        this.flags.clear();
        next.forEach((flags, mesh) => this.flags.set(mesh, flags));
        mask.renderList = [...next.keys()];
        this.ensurePostProcess();
    }

    /** The plain color a mesh is drawn into the mask with: its effect ring's slot in red, its target ring's in green (each slot / 4). */
    private maskMaterial(flags: MaskFlags): StandardMaterial {
        const key = `${flags.effect}${flags.target}`;
        let material = this.maskMaterials.get(key);
        if (!material) {
            material = new StandardMaterial(`mesh-outline-mask-${key}`, this.scene);
            material.disableLighting = true;
            material.emissiveColor = new Color3(flags.effect / MESH_OUTLINE_COLOR_SLOTS, flags.target / MESH_OUTLINE_COLOR_SLOTS, 0);
            material.diffuseColor = Color3.Black();
            material.specularColor = Color3.Black();
            material.fogEnabled = false;
            this.maskMaterials.set(key, material);
        }
        return material;
    }

    private ensureMask(): RenderTargetTexture {
        if (this.mask) return this.mask;
        const engine = this.scene.getEngine();
        const mask = new RenderTargetTexture('mesh-outline-mask', { width: engine.getRenderWidth(), height: engine.getRenderHeight() }, this.scene, {
            generateMipMaps: false,
            samplingMode: Constants.TEXTURE_NEAREST_SAMPLINGMODE,
            generateDepthBuffer: true,
        });
        mask.clearColor = new Color4(0, 0, 0, 0);
        mask.wrapU = Constants.TEXTURE_CLAMP_ADDRESSMODE;
        mask.wrapV = Constants.TEXTURE_CLAMP_ADDRESSMODE;
        this.scene.customRenderTargets.push(mask);
        this.mask = mask;
        // Each frame: the mask keeps the canvas's size, and the rings follow the camera the scene draws with.
        this.beforeRender = this.scene.onBeforeRenderObservable.add(() => this.followView());
        return mask;
    }

    private ensurePostProcess(): void {
        const camera = this.scene.activeCamera;
        if (!camera || !this.mask) return;
        if (this.postProcess && this.camera === camera) return;
        this.postProcess?.dispose(this.camera ?? undefined);
        const mask = this.mask;
        const postProcess = new PostProcess('mesh-outline', 'meshOutline', {
            uniforms: ['texelSize', 'effectWidth', 'targetWidth', ...SLOT_NUMBERS.flatMap((slot) => [`effectColor${slot}`, `targetColor${slot}`])],
            samplers: ['maskSampler'],
            size: 1,
            camera,
            samplingMode: Constants.TEXTURE_NEAREST_SAMPLINGMODE,
            // `shaderLanguage` must be explicit: Babylon never infers WGSL from the engine.
            shaderLanguage: this.scene.getEngine().isWebGPU ? ShaderLanguage.WGSL : ShaderLanguage.GLSL,
        });
        // The scene now draws into this pass's input: multisample it, or edges go jagged whenever anything is outlined.
        postProcess.samples = MSAA_SAMPLES;
        // Off the camera until its shader compiles: Babylon still draws the
        // scene into an unready pass's input but nothing to the canvas, so
        // every first hover would black out a frame. `followView` puts it on.
        camera.detachPostProcess(postProcess);
        this.isAttached = false;
        postProcess.onApply = (effect): void => {
            const size = mask.getSize();
            effect.setTexture('maskSampler', mask);
            effect.setFloat2('texelSize', 1 / Math.max(1, size.width), 1 / Math.max(1, size.height));
            for (const rank of ['effect', 'target'] as const) {
                SLOT_NUMBERS.forEach((slot) => {
                    const ring = this.ringColors[rank][slot - 1];
                    effect.setFloat4(`${rank}Color${slot}`, ring?.color.r ?? 0, ring?.color.g ?? 0, ring?.color.b ?? 0, ring?.alpha ?? 0);
                });
            }
            effect.setFloat('effectWidth', this.widths.effect);
            effect.setFloat('targetWidth', this.widths.target);
        };
        this.postProcess = postProcess;
        this.camera = camera;
    }

    private followView(): void {
        const mask = this.mask;
        if (!mask) return;
        const engine = this.scene.getEngine();
        const size = mask.getSize();
        if (size.width !== engine.getRenderWidth() || size.height !== engine.getRenderHeight()) {
            mask.resize({ width: engine.getRenderWidth(), height: engine.getRenderHeight() });
        }
        if (this.scene.activeCamera !== this.camera) {
            this.ensurePostProcess();
        }
        if (this.postProcess && this.camera && !this.isAttached && this.postProcess.isReady()) {
            this.camera.attachPostProcess(this.postProcess);
            this.isAttached = true;
        }
    }

    /** Stops drawing: no mask, no post-process, until something is outlined again. */
    private release(): void {
        if (this.beforeRender) {
            this.scene.onBeforeRenderObservable.remove(this.beforeRender);
            this.beforeRender = null;
        }
        this.postProcess?.dispose(this.camera ?? undefined);
        this.postProcess = null;
        this.camera = null;
        this.isAttached = false;
        if (this.mask) {
            const index = this.scene.customRenderTargets.indexOf(this.mask);
            if (index >= 0) this.scene.customRenderTargets.splice(index, 1);
            this.mask.dispose();
            this.mask = null;
        }
    }
}
